import './AppChat.scss';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react'
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { ChatMessage } from '../../models/LLM';
import { useAuth } from '../../contexts/auth';
import { Flex } from 'antd';
import { Col, Row } from 'react-bootstrap';
import MessageCloud from '../generics/MessageCloud';
import TypingLoader from '../generics/TypingLoader';
import ChatInput from '../generics/ChatInput';
import client from '../../api/axios';
import { EventEmitter } from '../../utils/EventEmitter';

type Props = {
  projectId?: string;
  show: boolean;
  onClose: VoidFunction;
}

type CompletionBody = {
  model: string;
  messages: Array<{ role: string; content: string }>;
  metadata: { thread_id?: string };
  user_id: string;
  assessment_id?: string;
  use_case: string;
  stream?: boolean;
  temperature?: number;
  max_tokens?: number;
}

type CompletionResponse = {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{ index: number; message: { role: string, content: string }, finish_reason: string }>;
  usage: any;
  thread_id: string;
}

const chatEndpoint = window.__env.AI_CHAT__ENDPOINT;
const modelId = window.__env.AI_CHAT__MODEL_ID;

export default function AppChat({ projectId, show, onClose }: Props) {
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error] = useState(""); // setError needed?
  const [prompt, setPrompt] = useState("");
  const [threadId, setThreadId] = useState("");
  const [projectUseCase, setProjectUseCase] = useState<string>();
  const [expanded, setExpanded] = useState(false);

  const { loggedUser } = useAuth();

  const scrollChatToBottom = useCallback(() => {
    const chatContainer = document.getElementById('chat-history-container');
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, []);

  useEffect(scrollChatToBottom, [history, scrollChatToBottom]);

  useEffect(() => {
    EventEmitter.subscribe('projectUseCase', setProjectUseCase);
    return () => EventEmitter.unsubscribe('projectUseCase');
  }, []);

  const handleSendMessage = useCallback((text: string) => {
    if (!chatEndpoint || !modelId) {
      setHistory(h => h.concat(message));
    }

    const message: ChatMessage = {
      text,
      isUserPrompt: true,
      timestamp: new Date().toISOString(),
    };
    setPrompt('');
    setLoading(true);
    setHistory(h => h.concat(message));

    const body: CompletionBody = {
      model: modelId,
      messages: [{ role: 'user', content: text }],
      metadata: {},
      user_id: loggedUser!.sub!,
      assessment_id: projectId || "",
      use_case: projectId ? projectUseCase || 'media' : "",
    };
    if (threadId) body.metadata.thread_id = threadId;

    client.post<CompletionResponse>(chatEndpoint, body)
      .then(
        ({ data }) => {
          setLoading(false);
          setThreadId(data.thread_id);
          setHistory(h => {
            return h.concat({
              text: data.choices[0].message.content,
              timestamp: new Date().toISOString(),
              isAlert: data.choices[0].message.role === "system"
            });
          });
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          setHistory(h => {
            return h.concat({
              text: `An error occurred${status ? ` (${status})` : ''}: ${message}`,
              timestamp: new Date().toISOString(),
              isAlert: true
            });
          });
        }
      )
  }, [loggedUser, projectId, threadId, projectUseCase]);

  const toggleExpanded = useCallback(() => setExpanded(v => !v), []);
  const handlePromptChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => setPrompt(evt.target.value), []);

  return (
    <nav className={classNames("app-chat", { open: show })}>
      <div className={classNames("chat-container", { expanded })}>
        <div className="chat-header">
          <Flex align="center" justify="space-between">
            <Flex align="center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="title-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
              </svg>
              <h6 className="ms-1 mb-0">AI Chat</h6>
            </Flex>

            <Flex gap={8}>
              <IconButton tooltip={{ mouseEnterDelay: 0.5, title: expanded ? "Reduce" : "Expand" }} className="p-1" onClick={toggleExpanded}>
                <FontAwesomeIcon icon={expanded ? solid('down-left-and-up-right-to-center') : solid('up-right-and-down-left-from-center')} />
              </IconButton>
              <IconButton tooltip="Close" className="ms-auto p-1" onClick={onClose}>
                <FontAwesomeIcon icon={solid('times')} />
              </IconButton>
            </Flex>
          </Flex>
        </div>

        <div className="chat-history" id="chat-history-container">
          {history.length === 0 && !error && (
            <Flex justify="center" className="py-5">
              <div className="chat-alert empty">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={0.75} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                </svg>
                <p className="text-center mt-3">Hi, how can I help you?</p>
              </div>
            </Flex>
          )}

          {error && (
            <Flex justify="center" className="py-5">
              <div className="chat-alert error">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>

                <p className="text-center mt-3">{error}</p>
              </div>
            </Flex>
          )}

          {history.map(message => (
            <Row className="mx-0 py-1" key={message.timestamp}>
              <MessageCloud message={message} />
            </Row>
          ))}

          {loading && (
            <Row className="mx-0 py-1">
              <MessageCloud>
                <TypingLoader />
              </MessageCloud>
            </Row>
          )}
        </div>

        <Row className="chat-toolbar mx-0">
          <Col>
            <ChatInput
              prompt={prompt}
              onPromptChange={handlePromptChange}
              onSendMessage={handleSendMessage}
            />
            <input className="d-none" type="file" id="file-upload-input" />
            <small className="text-danger font-xs">
              <b className="me-1">You are interacting with an AI system, not a human.</b>
              Responses may be inaccurate or incomplete. Please avoid sharing sensitive personal data unless necessary.
            </small>
          </Col>
        </Row>
      </div>
    </nav>
  )
}